import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from 'store';
import {
  setActiveUnit,
  setActiveUnitTitle,
  setShowFlowProperties,
} from 'store/units/reducer';
import DisplayCard from './DisplayCard';
import Notes from 'components/Notes';
import {
  setOpenPanel,
  setShowReportPanel,
  setShowSideCard,
} from 'store/layout/reducer';
import SidePanelHeader, { createCloseButton } from 'components/SidePanelHeader';
import { getShowReportPanel } from 'store/settings/selectors';
import { getActiveUnit } from 'store/units/selectors';
import {
  hasFlowProperties,
  hasReport,
  supportsChamberAnalysis,
} from 'containers/ReportPanel/utils';
import { getShowReportButton, getShowSideCard } from 'store/layout/selectors';
import { SidePanelActionBar } from 'components/SidePanelActionBar';
import { UnitTypes } from 'unitData/types';
import { removeUnits } from 'store/units/sagaActions';
import { removeConnections } from 'store/connections/sagaActions';
import React, { useEffect } from 'react';
import ChamberViewerTourProvider, {
  useChamberViewerTour,
} from 'tour/chamberViewerTourContext';
import ChamberViewerTourPopover from 'tour/ChamberViewerTourPopover';
import { useIsExternal } from 'auth/useIsExternal';
import { TourOverlay } from 'tour/TourOverlay';
import { chamberEvent } from 'analytics/events';
import { PROPERTY_PANEL_WIDTH } from '../constants';

const Container = styled('div')`
  width: ${PROPERTY_PANEL_WIDTH}px;
  height: 100vh;
  min-width: ${PROPERTY_PANEL_WIDTH}px;
  max-width: ${PROPERTY_PANEL_WIDTH}px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing(3)};
`;

const Content = styled('div')`
  overflow-y: auto;
  flex-grow: 1;
  > * {
    margin-bottom: 24px;
  }
`;

type Props = {
  title: string;
  notes?: React.ReactNode;
  displayCard?: React.ReactNode;
  children: React.ReactNode;
  showActionBar?: boolean;
  actionBar?: React.ReactNode;
};

const PropertyPanelContent = ({
  title,
  notes = <Notes />,
  displayCard = <DisplayCard />,
  children,
  showActionBar,
  actionBar,
}: Props) => {
  const dispatch = useDispatch();
  const activeUnit = useSelector(getActiveUnit);
  const showReportPanel = useSelector(getShowReportPanel);
  const showReportButton = useSelector(getShowReportButton);
  const showSideCard = useSelector(getShowSideCard);
  const isExternal = useIsExternal();
  const { showFlowProperties } = useSelector(
    (state: RootState) => state.unitConnGroup.present.unitState,
  );

  useEffect(() => {
    if (!showFlowProperties && activeUnit) {
      dispatch(setActiveUnitTitle(title));
    }
  }, [activeUnit, dispatch, showFlowProperties, title]);

  const onClose = () => {
    dispatch(setActiveUnit(null));
    dispatch(setOpenPanel(null));
    dispatch(setShowSideCard(false));
  };

  const onShowFlowProperties =
    activeUnit && hasFlowProperties(activeUnit.type)
      ? () => {
          dispatch(setShowReportPanel(false));
          dispatch(setShowFlowProperties(true));
        }
      : null;

  const onShowReport =
    activeUnit && hasReport(activeUnit) && showReportButton
      ? () => {
          dispatch(setShowReportPanel(!showReportPanel));
        }
      : null;

  const onShowChamberAnalysis =
    activeUnit && supportsChamberAnalysis(activeUnit.type)
      ? () => {
          if (!showSideCard) {
            chamberEvent('chamber_viewer_opened', {
              unit_type: activeUnit.type,
            });
          }
          dispatch(setShowSideCard(!showSideCard));
        }
      : null;

  const onDelete =
    activeUnit?.type === UnitTypes.FREE_TEXT_BOX
      ? () => {
          dispatch(removeUnits([activeUnit.key]));
          dispatch(removeConnections(activeUnit.key));
        }
      : null;

  const content = (
    <Container>
      <SidePanelHeader
        testId='propertypanel-header'
        title={title}
        trailing={createCloseButton(onClose)}
      />
      {actionBar ??
        (showActionBar && (
          <SidePanelActionBar
            onShowFlowProperties={onShowFlowProperties}
            onShowReport={onShowReport}
            onShowChamberAnalysis={onShowChamberAnalysis}
            onDelete={onDelete}
          />
        ))}
      <Content>
        {children}
        {displayCard !== null && displayCard}
        {notes !== null && notes}
      </Content>
    </Container>
  );

  if (onShowChamberAnalysis && !isExternal && showSideCard) {
    return (
      <ChamberViewerTourProvider>
        <TourOverlay useTourHook={useChamberViewerTour}>
          <ChamberViewerTourPopover />
          {content}
        </TourOverlay>
      </ChamberViewerTourProvider>
    );
  }

  return content;
};

export default PropertyPanelContent;
